import React from "react";
import Button from 'react-bootstrap/Button';
import Nav from 'react-bootstrap/Nav';
import Offcanvas from 'react-bootstrap/Offcanvas';
import {
    FaBars,
    FaCalendarAlt,
    FaClipboardList,
    FaHome,
    FaHospitalUser,
    FaHouseUser,
    FaNotesMedical,
    FaPowerOff,
    FaRegClock,
    FaUserAlt
} from 'react-icons/fa';
import { Link, Redirect, Route } from "react-router-dom";
import { useStateValue } from '../../state';
import { sessionService } from '../../services/session';
import {
    canAccessEvolutions,
    canAccessPatients,
    canAccessPoint,
    canViewSchedule,
    canViewPartners,
    getAccessLabel,
} from '../../utils/access';

const MENU_ITEMS = [
    {
        path: "/",
        icon: <FaHome />,
        title: "Inicio",
        description: "Resumo do portal",
        id: 0,
        tipoAcesso: 0
    },
    {
        path: "/partner",
        icon: <FaHospitalUser />,
        title: "Usuarios",
        description: "Equipe e consulta",
        id: 1,
        isVisible: (acesso) => canViewPartners(acesso)
    },
    {
        path: "/patient",
        icon: <FaHouseUser />,
        title: "Pacientes",
        description: "Carteira e leitura",
        id: 2,
        isVisible: (acesso) => canAccessPatients(acesso)
    },
    {
        path: "/schedule",
        icon: <FaCalendarAlt />,
        title: "Agenda",
        description: "Atendimentos do dia",
        id: 3,
        isVisible: (acesso) => canViewSchedule(acesso)
    },
    {
        path: "/Partnerschedule",
        icon: <FaRegClock />,
        title: "Ponto",
        description: "Entrada e saida",
        id: 4,
        isVisible: (acesso) => canAccessPoint(acesso)
    },
    {
        path: "/evolution",
        icon: <FaNotesMedical />,
        title: "Evolucoes",
        description: "Prontuario clinico restrito",
        id: 5,
        isVisible: (acesso) => canAccessEvolutions(acesso)
    }
];

const QUICK_ACTIONS = [
    {
        path: "/schedule",
        icon: <FaCalendarAlt />,
        title: "Agenda hoje",
        isVisible: (acesso) => canViewSchedule(acesso)
    },
    {
        path: "/evolution",
        icon: <FaNotesMedical />,
        title: "Nova evolucao",
        isVisible: (acesso) => canAccessEvolutions(acesso)
    },
    {
        path: "/patient",
        icon: <FaClipboardList />,
        title: "Ver pacientes",
        isVisible: (acesso) => canAccessPatients(acesso)
    }
];

function isItemVisible(item, accessLevel) {
    if (typeof item.isVisible === 'function') {
        return item.isVisible(accessLevel);
    }

    if (typeof item.tipoAcesso === 'number') {
        return accessLevel >= item.tipoAcesso;
    }

    return true;
}

function isActiveRoute(currentPath, itemPath) {
    if (itemPath === "/") {
        return currentPath === "/";
    }

    return currentPath.startsWith(itemPath);
}

const AppLayout = ({ component: Component, ...rest }) => {
    const [sidebarOpen, setSidebarOpen] = React.useState(false);
    const acesso = sessionService.getAccessLevel();
    const [{ authentication }, dispatch] = useStateValue();
    const visibleMenu = MENU_ITEMS.filter((item) => isItemVisible(item, acesso));
    const visibleQuickActions = QUICK_ACTIONS.filter((item) => isItemVisible(item, acesso));

    const handleLogout = () => {
        setSidebarOpen(false);
        dispatch({ type: 'logout' });
    };

    const renderSidebar = (currentPath) => (
        <div className="portal-sidebar-inner">
            <div className="portal-sidebar-brand">
                <div className="portal-sidebar-brand-mark">PS</div>
                <div>
                    <strong>Clinica Psyquie</strong>
                    <span>Portal de atendimentos</span>
                </div>
            </div>

            <div className="portal-sidebar-profile">
                <div className="portal-sidebar-avatar">
                    <FaUserAlt />
                </div>
                <div>
                    <strong>{sessionService.getDisplayName()}</strong>
                    <span>{getAccessLabel(acesso)}</span>
                </div>
            </div>

            <div className="portal-sidebar-group">
                <span className="portal-sidebar-group-title">Navegacao</span>
                <Nav className="portal-sidebar-nav">
                    {visibleMenu.map((item) => (
                        <Nav.Link
                            as={Link}
                            className={`portal-sidebar-link ${isActiveRoute(currentPath, item.path) ? 'is-active' : ''}`}
                            key={item.id}
                            onClick={() => setSidebarOpen(false)}
                            to={item.path}
                        >
                            <span className="portal-sidebar-link-icon">{item.icon}</span>
                            <span className="portal-sidebar-link-copy">
                                <strong>{item.title}</strong>
                                <small>{item.description}</small>
                            </span>
                        </Nav.Link>
                    ))}
                </Nav>
            </div>

            <div className="portal-sidebar-group">
                <span className="portal-sidebar-group-title">Atalhos</span>
                <div className="portal-sidebar-shortcuts">
                    {visibleQuickActions.map((item) => (
                        <Link
                            className="portal-shortcut"
                            key={item.path}
                            onClick={() => setSidebarOpen(false)}
                            to={item.path}
                        >
                            <span>{item.icon}</span>
                            <strong>{item.title}</strong>
                        </Link>
                    ))}
                </div>
            </div>

            <div className="portal-sidebar-footer">
                <Button className="portal-logout-btn" onClick={handleLogout} variant="light">
                    <FaPowerOff /> Sair
                </Button>
            </div>
        </div>
    );

    return (
        <Route
            {...rest}
            render={matchProps => (
                authentication ? (
                    <div className="portal-app">
                        <aside className="portal-sidebar d-none d-lg-flex">
                            {renderSidebar(matchProps.location.pathname)}
                        </aside>

                        <Offcanvas
                            className="portal-sidebar-offcanvas"
                            onHide={() => setSidebarOpen(false)}
                            placement="start"
                            show={sidebarOpen}
                        >
                            <Offcanvas.Header closeButton />
                            <Offcanvas.Body>
                                {renderSidebar(matchProps.location.pathname)}
                            </Offcanvas.Body>
                        </Offcanvas>

                        <div className="portal-main">
                            <header className="portal-mobilebar d-lg-none">
                                <Button
                                    className="portal-mobilebar-btn"
                                    onClick={() => setSidebarOpen(true)}
                                    variant="light"
                                >
                                    <FaBars />
                                </Button>

                                <Link className="portal-mobilebar-brand" to="/">
                                    Clinica Psyquie
                                </Link>

                                <Button
                                    className="portal-mobilebar-btn"
                                    onClick={handleLogout}
                                    variant="light"
                                >
                                    <FaPowerOff />
                                </Button>
                            </header>

                            <main className="portal-main-content">
                                <Component {...matchProps} authentication={authentication} />
                            </main>
                        </div>
                    </div>
                ) : (
                    <Redirect to='/login' />
                )
            )}
        />
    );
};

export default AppLayout;
